'use client';

import React from 'react';
import { Plus, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';

interface CellExpandableProps {
  isExpanded: boolean;
  onToggle: () => void;
  className?: string;
}

/**
 * CellExpandable Component
 *
 * Renders an expandable row toggle control (plus/minus icons).
 * Used in DataTable for expandable rows feature.
 *
 * @example
 * <CellExpandable
 *   isExpanded={expandedRows.has(rowKey)}
 *   onToggle={() => handleToggleRow(rowKey)}
 * />
 */
export const CellExpandable: React.FC<CellExpandableProps> = ({
  isExpanded,
  onToggle,
  className,
}) => {
  return (
    <button
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
      className={cn(
        'inline-flex items-center justify-center',
        'text-slate-400 hover:text-slate-600',
        'transition-all duration-200',
        'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded',
        'p-1',
        className
      )}
      aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
      aria-expanded={isExpanded}
    >
      {isExpanded ? (
        <Minus className="h-4 w-4" />
      ) : (
        <Plus className="h-4 w-4" />
      )}
    </button>
  );
};
